import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetProviderDirectoryServiceQuery,
  useUpdateProviderDirectoryServiceMutation,
} from '@/services/api/providerDirectoryV2Service';

const SERVICE_TYPES = [
  'ER',
  'RADIOLOGY',
  'CHIROPRACTIC',
  'ORTHOPEDIC',
  'PHYSICAL_THERAPY',
  'PRIMARY_CARE',
  'PHARMACY',
  'IMAGING',
  'SURGERY',
  'SPECIALIST',
  'GENERAL',
] as const;

export const EditServiceModal = ({
  open,
  onClose,
  serviceId,
}: {
  open: boolean;
  onClose: () => void;
  serviceId: string;
}) => {
  const { data: service } = useGetProviderDirectoryServiceQuery(
    { serviceId },
    { skip: !open || !serviceId },
  );
  const [updateService, { isLoading: isSaving }] =
    useUpdateProviderDirectoryServiceMutation();

  const [name, setName] = useState('');
  const [serviceType, setServiceType] = useState<string>('GENERAL');
  const [portalEligible, setPortalEligible] = useState(false);
  const [portalType, setPortalType] = useState('');
  const [aliasesText, setAliasesText] = useState('');

  useEffect(() => {
    if (!open || !service) return;
    setName(service.name ?? '');
    setServiceType(service.service_type ?? 'GENERAL');
    setPortalEligible(Boolean(service.portal_eligible));
    setPortalType(Array.isArray(service.portal_type) ? service.portal_type.join(', ') : (service.portal_type ?? ''));
    setAliasesText((service.aliases ?? []).join(', '));
  }, [open, service]);

  const aliases = useMemo(() => {
    const raw = aliasesText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return raw.length ? raw : [];
  }, [aliasesText]);

  const canSave = Boolean(name.trim());

  const handleSave = async () => {
    if (!service) return;
    if (!canSave) return;
    try {
      await updateService({
        serviceId: service.id,
        patch: {
          name: name.trim(),
          service_type: serviceType || null,
          portal_eligible: portalEligible,
          portal_type: portalType.trim() ? portalType.split(',').map(s => s.trim()).filter(Boolean) : null,
          aliases,
        },
      }).unwrap();
      toast.success('Service updated');
      onClose();
    } catch (err) {
      console.error('Failed to update service', err);
      toast.error('Failed to update service');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent className="w-[calc(100vw-24px)] max-w-2xl max-h-[calc(100vh-24px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit service</DialogTitle>
          <DialogDescription>
            This is the display name used in search and routing labels.
          </DialogDescription>
        </DialogHeader>

        {!service ? (
          <div className="text-sm text-zinc-600">Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="text-xs font-medium text-zinc-700">
                Service name
              </div>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="text-xs font-medium text-zinc-700">
                  Service type
                </div>
                <select
                  className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm w-full"
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                >
                  {SERVICE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-medium text-zinc-700">Aliases</div>
                <Input
                  value={aliasesText}
                  onChange={(e) => setAliasesText(e.target.value)}
                  placeholder="Comma-separated…"
                />
              </div>
            </div>

            <div className="rounded-xl border border-zinc-200 bg-white p-4 space-y-3">
              <label
                htmlFor="provider-directory-service-portal-eligible"
                className="flex items-center gap-2 text-sm text-zinc-700"
              >
                <input
                  id="provider-directory-service-portal-eligible"
                  type="checkbox"
                  checked={portalEligible}
                  onChange={(e) => setPortalEligible(e.target.checked)}
                />
                <span>Portal eligible</span>
              </label>
              <div className="space-y-2">
                <div className="text-xs font-medium text-zinc-700">
                  Portal type
                </div>
                <Input
                  value={portalType}
                  onChange={(e) => setPortalType(e.target.value)}
                  placeholder="Optional…"
                  disabled={!portalEligible}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!canSave || isSaving}>
                Save
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
